import S, { ObjectSchema } from 'fluent-json-schema';
import { produce } from 'immer';
import isEmpty from 'lodash/isEmpty';

import { normalizeWhiteSpace } from '@eluve/utils';

import { Block, DynamicArtifactTemplate } from './dynamic-artifact.schema';
import { ensureUniqueKeys } from './truncate-key';

export const convertDynamicArtifactToJsonSchema = (
  artifact: DynamicArtifactTemplate,
  options: { strict?: boolean } = {},
): ObjectSchema<Record<string, any>> => {
  const { blocks } = artifact;
  const { strict = true } = options;
  const schema = S.object()
    .description(artifact.description ?? artifact.name)
    .additionalProperties(false);

  /* Only a subset of JSON schema is supported: https://platform.openai.com/docs/guides/structured-outputs/supported-schemas
   *
   * Unsupported as of 10/17/2024
   * For strings: minLength, maxLength, pattern, format
   * For numbers: minimum, maximum, multipleOf
   * For objects: patternProperties, unevaluatedProperties, propertyNames, minProperties, maxProperties
   * For arrays: unevaluatedItems, contains, minContains, maxContains, minItems, maxItems, uniqueItems
   */
  const convertBlocksToJsonSchema = (blocks: Block[], schema: ObjectSchema) => {
    for (const block of blocks) {
      const { key, isRequired = false } = block;

      const description = !isEmpty(block.description)
        ? block.description
        : block.importedDescription;
      const isAiEnabled = block.isAiEnabled ?? true;

      if (!isAiEnabled) {
        continue;
      }

      switch (block.type) {
        case 'group': {
          let groupObj = S.object().additionalProperties(false);

          groupObj = description ? groupObj.description(description) : groupObj;

          const result = convertBlocksToJsonSchema(block.blocks, groupObj);

          schema = schema.prop(key, result);
          if (strict) {
            schema = schema.required();
          }

          break;
        }
        case 'text': {
          let prop = S.string();
          prop = description ? prop.description(description) : prop;
          schema = schema.prop(
            key,
            isRequired ? prop : S.anyOf([prop, S.null()]),
          );

          if (strict) {
            schema = schema.required();
          }
          break;
        }
        case 'checkbox': {
          const { options } = block;
          const optionLabels = options
            ? options.map((option: { label: string }) =>
                normalizeWhiteSpace(option.label),
              )
            : [];

          let prop = S.array().items(
            S.object()
              .additionalProperties(false)
              .prop('label', S.string().enum(optionLabels))
              .prop('isChecked', S.boolean())
              .required(['label', 'isChecked']),
          );

          prop = description ? prop.description(description) : prop;

          schema = schema.prop(key, prop);
          if (strict) {
            schema = schema.required();
          }

          break;
        }
        case 'range': {
          const { min, max } = block;
          let prop = S.number();
          const rangeDefaultDescription = `Range from ${min} to ${max}.`;
          const propDescription = description
            ? `${rangeDefaultDescription} ${description}`
            : rangeDefaultDescription;
          prop = prop.description(propDescription);

          schema = schema.prop(
            key,
            isRequired ? prop : S.anyOf([prop, S.null()]),
          );
          if (strict) {
            schema = schema.required();
          }

          break;
        }
      }
    }
    return schema;
  };

  const processedBlocks = produce(blocks, (draft) => {
    // Ensure all keys are unique and within Claude's 64-character limit
    // This replaces the addEluvePostfix logic since ensureUniqueKeys handles both
    // truncation and duplicate key resolution
    ensureUniqueKeys(draft);
  });

  const resultSchema = convertBlocksToJsonSchema(processedBlocks, schema);

  return resultSchema;
};
